import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { Subscription, interval } from 'rxjs';
import { ReportDelivery, ReportDeliveryStatus } from 'ldt-data-deliveries-api';
import { DownloadStatusService } from './download-status.service';
import { DownloadsService } from 'src/app/downloads/downloads.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-download-status',
    templateUrl: './download-status.component.html',
    animations: [
        trigger('slideInOut', [
            transition(':enter', [
                style({ transform: 'translateY(100%)' }),
                animate('300ms ease-out', style({ transform: 'translateY(0)' })),
            ]),
            transition(':leave', [animate('300ms ease-in', style({ transform: 'translateY(100%)' }))]),
        ]),
    ],
    standalone: false
})
export class DownloadStatusComponent implements OnInit, OnDestroy {
  @Input() download: ReportDelivery;
  visible = true;
  progress = 0;
  downloadSubscription: Subscription;
  autoDownload = true;
  isDownloaded = false;
  private loaderInterval: Subscription;
  currentLoaderIcon = 'hourglass_empty';
  ReportDeliveryStatus = ReportDeliveryStatus;

  private readonly loaderIcons = [
    'hourglass_empty',
    'hourglass_bottom',
    'hourglass_top',
    'hourglass_full',
  ];

  constructor(
    private downloadStatusService: DownloadStatusService,
    private downloadsService: DownloadsService,
    private router: Router
  ) {}

  getDownloadsUrl(): string {
    const orgId = this.router.url.split('/')[1];
    return `/${orgId}/downloads/downloads-list`;
  }

  ngOnInit(): void {
    this.downloadSubscription = this.downloadStatusService.activeDownload$.subscribe((delivery) => {
      if (delivery) {
        if (this.download && this.download.id !== delivery.id) {
          this.visible = true;
        }
        if (
          this.download.status !== delivery.status &&
          delivery.status === ReportDeliveryStatus.Complete
        ) {
          if (this.autoDownload) {
            this.downloadsService.downloadFile(delivery);
          }
        }
        this.download = delivery;
        this.updateLoaderAnimation();
      }
    });
  }

  private updateLoaderAnimation(): void {
    if (this.loaderInterval) {
      this.loaderInterval.unsubscribe();
    }

    if (this.download?.status === ReportDeliveryStatus.InProgress) {
      let currentIndex = 0;
      this.currentLoaderIcon = this.loaderIcons[0];

      this.loaderInterval = interval(800).subscribe(() => {
        currentIndex = (currentIndex + 1) % this.loaderIcons.length;
        this.currentLoaderIcon = this.loaderIcons[currentIndex];
      });
    }
  }

  downloadFile(): void {
    this.downloadsService.downloadFile(this.download);
  }

  ngOnDestroy(): void {
    if (this.downloadSubscription) {
      this.downloadSubscription.unsubscribe();
    }
    if (this.loaderInterval) {
      this.loaderInterval.unsubscribe();
    }
  }

  close(): void {
    this.visible = false;
  }

  getStatusIcon(): string {
    if (this.isDownloaded) {
      return 'check_circle';
    }

    switch (this.download.status) {
      case ReportDeliveryStatus.Complete:
        return 'check';
      case ReportDeliveryStatus.InProgress:
        return this.currentLoaderIcon;
      case ReportDeliveryStatus.Failed:
        return 'error_outline';
      case ReportDeliveryStatus.Scheduled:
        return 'hourglass_empty';
      default:
        return 'info_outline';
    }
  }

  getStatusClass(): string {
    if (this.isDownloaded) {
      return 'tw-text-green-500';
    }

    switch (this.download.status!) {
      case ReportDeliveryStatus.Complete:
        return 'tw-text-blue-500';
      case ReportDeliveryStatus.InProgress:
        return 'tw-text-blue-500';
      case ReportDeliveryStatus.Failed:
        return 'tw-text-red-500';
      case ReportDeliveryStatus.Scheduled:
        return 'tw-text-gray-500';
      default:
        return 'tw-text-gray-500';
    }
  }

  getStatusBgClass(): string {
    switch (this.download.status!) {
      case ReportDeliveryStatus.Complete:
        return 'tw-bg-blue-100 dark:tw-bg-blue-900';
      case ReportDeliveryStatus.InProgress:
        return 'tw-bg-blue-100 dark:tw-bg-blue-900';
      case ReportDeliveryStatus.Failed:
        return 'tw-bg-red-100 dark:tw-bg-red-900';
      case ReportDeliveryStatus.Scheduled:
        return 'tw-bg-gray-100 dark:tw-bg-gray-900';
      default:
        return 'tw-bg-gray-100 dark:tw-bg-gray-900';
    }
  }

  getHeadingText(): string {
    switch (this.download.status) {
      case ReportDeliveryStatus.Complete:
        return 'Download is Complete';
      case ReportDeliveryStatus.InProgress:
        return 'Download in Progress';
      case ReportDeliveryStatus.Failed:
        return 'Download Failed';
      case ReportDeliveryStatus.Scheduled:
        return 'Download is Scheduled';
      default:
        return 'Download Status';
    }
  }

  getStatusText(): string {
    if (this.isDownloaded) {
      return 'Downloaded';
    }
    return this.download.status;
  }
}
